import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';

// Deploy-time engine differentiators. These live in the rebalancer's ENV, so a
// toggle records the DESIRED state and the panel surfaces the exact command to
// apply it (the engine restarts with the flag). Usage-aware is intentionally NOT
// here — it's live via the RebalancerConfig CRD (UsageAwarePanel).
const BOOL_FEATURES = [
  { key: 'global_repack', label: 'Whole-cluster optimization',
    desc: 'Plan node removals against the entire fleet, not one node at a time.',
    tip: 'Global re-solve (CAST-style): finds consolidation combinations the per-node search misses. Same safety gates and pacing.' },
  { key: 'clm', label: 'Live migration',
    desc: 'Move pods to new nodes without restarting them.',
    tip: 'CRIU state migration (CLM) instead of eviction. Needs the agent CLM coordinator.' },
  { key: 'clm_consolidation', label: 'Consolidate pinned pods',
    desc: 'Free up nodes running protected or stateful pods by live-migrating them.',
    tip: 'Live-migrates do-not-disrupt / stateful pods. Needs a CLM green pool.' },
  { key: 'spot_risk', label: 'Spot risk awareness',
    desc: 'Avoid instance types with a high interruption risk.',
    tip: 'Weights candidate shapes by predicted spot interruption risk per family and zone.' },
  { key: 'zone_spread', label: 'Zone spread protection',
    desc: 'Never consolidate in a way that violates zone spread rules.',
    tip: 'Models DoNotSchedule topology spread so consolidation does not strand pods.' },
  { key: 'aggressive_evictor', label: 'Aggressive consolidation',
    desc: 'Allow single-replica and local-storage pods to be moved. Explicit protections still apply.',
    tip: 'Single-replica / bare / local-storage pods become consolidatable; explicit protections still win.' },
  { key: 'soft_taint', label: 'Gentle node drains',
    desc: 'Discourage new pods on draining nodes instead of blocking them.',
    tip: 'Drains apply a PreferNoSchedule taint instead of a hard cordon.' },
  { key: 'rightsize_feed', label: 'Rightsizing-aware consolidation',
    desc: 'Plan node removals at the size pods restart at, not the size they run at today.',
    tip: 'Feeds rightsizing recommendations into drain planning so a consolidation never forces a bigger replacement node. Pair with the admission webhook so pods land rightsized.' },
];
const COST_MODES = ['off', 'shadow', 'enforce'];
const COST_MODE_LABELS = { off: 'Off', shadow: 'Preview only', enforce: 'Enabled' };

function Toggle({ on, disabled, onClick }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onClick}
      className={[
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        on ? 'bg-emerald-500' : 'bg-slate-300',
        disabled ? 'opacity-50' : '',
      ].join(' ')}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </button>
  );
}

export default function EngineFeaturesPanel({ clusterId }) {
  const [features, setFeatures] = useState(null);
  const [applyCommand, setApplyCommand] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const hydrate = (data) => {
    setFeatures(data?.features || {});
    setApplyCommand(data?.apply_command || '');
  };

  const load = useCallback(async () => {
    if (!clusterId) return;
    try {
      const r = await axios.get(`${API}/v1/clusters/${clusterId}/node-optimization/engine-features`);
      hydrate(r.data);
    } catch { /* leave null */ }
  }, [clusterId]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    if (!clusterId) return;
    const prev = features;
    setFeatures({ ...features, ...patch });
    setBusy(true);
    try {
      const r = await axios.post(`${API}/v1/clusters/${clusterId}/node-optimization/engine-features`, patch);
      hydrate(r.data);
    } catch {
      setFeatures(prev);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(applyCommand);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };

  if (!clusterId || !features) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
      <div className="mb-1 flex items-baseline justify-between">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Advanced optimization</h3>
        <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">Requires restart to apply</span>
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
        These settings take effect after you run the command below on your cluster.
      </p>

      {/* Live pricing feed (mode) */}
      <div className="flex items-start justify-between gap-4 py-3 border-t border-slate-100 dark:border-slate-800">
        <div title="Per-zone spot and commitment pricing from the live cloud pricing feed. Covers GCP families the static catalog lacks (n2d, c3d, …).">
          <div className="text-sm font-medium text-slate-800 dark:text-slate-100">Live pricing feed</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Use real-time cloud pricing instead of the built-in catalog for more accurate savings estimates.
          </div>
        </div>
        <div className="relative shrink-0">
          <select
            value={features.cost_feed || 'off'}
            disabled={busy}
            onChange={(e) => save({ cost_feed: e.target.value })}
            className="h-8 appearance-none rounded-lg border border-slate-300 bg-white px-2.5 pr-7 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            {COST_MODES.map((m) => <option key={m} value={m}>{COST_MODE_LABELS[m]}</option>)}
          </select>
          <svg
            className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"
          >
            <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {/* Boolean differentiators */}
      {BOOL_FEATURES.map((f) => (
        <div key={f.key} className="flex items-start justify-between gap-4 py-3 border-t border-slate-100 dark:border-slate-800">
          <div title={f.tip}>
            <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{f.label}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{f.desc}</div>
          </div>
          <Toggle on={!!features[f.key]} disabled={busy} onClick={() => save({ [f.key]: !features[f.key] })} />
        </div>
      ))}

      {/* Apply command */}
      {applyCommand ? (
        <div className="mt-4 rounded-md bg-slate-900 p-3">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-medium text-slate-400">Run this on your cluster</span>
            <button onClick={copy} className="text-xs text-emerald-400 hover:text-emerald-300">
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <code className="block overflow-x-auto whitespace-pre text-xs text-slate-100">{applyCommand}</code>
        </div>
      ) : (
        <p className="mt-4 text-xs text-slate-400">All advanced features are off. Nothing to apply.</p>
      )}
    </div>
  );
}
